import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { LetterTemplateDialog } from '../components/LetterTemplateDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip as RTooltip, BarChart,
} from 'recharts';
import {
  Loader2, QrCode, Send, Eye, PhoneIncoming, PhoneOutgoing, PenLine, CalendarClock, Mic, Video, ChevronRight, ChevronDown, RotateCcw, Trash2, AlertTriangle, Mail, ThumbsUp, ThumbsDown, Reply, Plus, Timer, Linkedin, Flag, X, FileSpreadsheet, Phone,
} from 'lucide-react';
import { saveBlobResponse, blobErrorDetail } from '../lib/download';
import { SequencePerformanceCard } from '../components/SequencePerformanceCard';
import { MailboxScanCard } from '../components/MailboxScanCard';
import { TestScanAuditCard } from '../components/TestScanAuditCard';
import { VestaboardCard } from '../components/VestaboardCard';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const fmt = (iso) => iso ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
const pct = (a, b) => b > 0 ? Math.round((a / b) * 100) : 0;
const fmtDays = (d) => d == null ? '—' : d < 1 ? `${Math.max(1, Math.round(d * 24))}h` : `${Math.round(d * 10) / 10}d`;

const Kpi = ({ icon: Icon, label, value, sub, color = 'text-foreground' }) => (
  <div className="bg-card border border-border rounded-sm p-4">
    <div className="flex items-center gap-2 text-muted-foreground">
      <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
      <span className="text-[10px] uppercase tracking-wider">{label}</span>
    </div>
    <div className={`text-2xl font-heading mt-2 ${color}`}>{value}</div>
    {sub && <div className="text-[10px] font-mono text-muted-foreground mt-0.5">{sub}</div>}
  </div>
);


const NI_CAT_LABELS = { no_sale_interest: 'No interest in selling', timing: 'Timing', in_talks: 'Already in talks', solved: 'Succession already solved', other: 'Other', uncategorized: 'No category' };

function ResetAllDialog({ open, onOpenChange, data, onDone }) {
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);

  const doReset = async () => {
    setBusy(true);
    try {
      const res = await api.resetAllOutreachStats();
      const d = res.data;
      toast.success(`Statistics reset — ${d.visits_deleted} scans, ${d.leads_deleted} responses, ${d.engagement_deleted} sessions deleted, ${d.letters_reverted} letters back to Sent`);
      onOpenChange(false);
      setConfirmText('');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setConfirmText(''); }}>
      <DialogContent className="max-w-md" data-testid="outreach-reset-all-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm inline-flex items-center gap-2 text-red-400">
            <AlertTriangle className="w-4 h-4" /> Reset ALL outreach statistics
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-muted-foreground space-y-1.5">
          <p className="text-foreground/90">This permanently deletes:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><b className="text-foreground">{data?.scans?.total ?? 0} QR scans</b> incl. devices, times & locations</li>
            <li><b className="text-foreground">{data?.responses?.total ?? 0} responses</b> (callbacks & appointments)</li>
            <li>All engagement sessions (watch time, plays, website visits)</li>
            <li><b className="text-foreground">{data?.funnel?.letters_responded ?? 0} responded letter{(data?.funnel?.letters_responded ?? 0) !== 1 ? 's' : ''}</b> revert to status “Sent”</li>
            <li><b className="text-foreground">{data?.funnel?.phone_followups ?? 0} phone follow-up result{(data?.funnel?.phone_followups ?? 0) !== 1 ? 's' : ''}</b> incl. “no interest” reasons</li>
          </ul>
          <p className="pt-1">Letters and landing pages stay intact — all QR codes keep working.</p>
        </div>
        <div className="space-y-2">
          <p className="text-[11px] text-muted-foreground">Type <b className="text-red-400 font-mono">RESET</b> to confirm:</p>
          <input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="RESET"
            data-testid="outreach-reset-all-confirm-input"
            className="w-full bg-secondary/40 border border-border rounded-sm px-3 py-2 text-sm text-foreground font-mono focus:outline-none focus:border-red-700" />
          <button onClick={doReset} disabled={confirmText !== 'RESET' || busy}
            data-testid="outreach-reset-all-confirm-btn"
            className="w-full inline-flex items-center justify-center gap-2 bg-red-800 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-sm text-xs h-9 transition-colors">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
            Reset all statistics
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ManualResponseDialog({ open, onOpenChange, onDone }) {
  const [letters, setLetters] = useState([]);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(null);
  const [channel, setChannel] = useState('phone');
  const [sentiment, setSentiment] = useState('negative');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      api.listLetters().then(r => setLetters((r.data || []).filter(l => l.status === 'sent'))).catch(() => {});
      setSel(null); setQ(''); setNote(''); setChannel('phone'); setSentiment('negative');
    }
  }, [open]);

  const matches = (q.trim()
    ? letters.filter(l => (l.company_name || '').toLowerCase().includes(q.trim().toLowerCase()))
    : letters).slice(0, 30);

  const save = async () => {
    if (!sel) return;
    setBusy(true);
    try {
      await api.recordManualResponse(sel.id, { channel, sentiment, note });
      toast.success(`Response logged for ${sel.company_name}`);
      onOpenChange(false);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to log response'); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="manual-response-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm inline-flex items-center gap-2">
            <Reply className="w-4 h-4 text-sky-400" /> Log a response manually
          </DialogTitle>
        </DialogHeader>
        {!sel ? (
          <div className="space-y-2">
            <p className="text-[11px] text-muted-foreground">Pick the sent letter the owner responded to (e.g. after a call that was never captured):</p>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search company…" autoFocus
              data-testid="manual-response-search"
              className="w-full bg-secondary/40 border border-border rounded-sm px-3 py-2 text-sm text-foreground focus:outline-none focus:border-sky-700" />
            <div className="max-h-56 overflow-y-auto space-y-1">
              {matches.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2">No sent letters match.</p>
              ) : matches.map(l => (
                <button key={l.id} onClick={() => setSel(l)} data-testid={`manual-response-letter-${l.id}`}
                  className="w-full text-left px-3 py-2 border border-border/60 rounded-sm hover:bg-secondary/40 transition-colors">
                  <span className="text-xs text-foreground font-medium">{l.company_name || '—'}</span>
                  <span className="text-[10px] font-mono text-muted-foreground ml-2">sent {fmt(l.sent_at)}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-foreground">{sel.company_name}</span>
              <button onClick={() => setSel(null)} className="text-[10px] text-muted-foreground hover:text-foreground underline"
                data-testid="manual-response-change-letter">change</button>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Channel</p>
              <div className="flex flex-wrap gap-1.5">
                {[['phone', 'Phone', PhoneOutgoing], ['email', 'Email', Mail], ['inbound_call', 'Inbound call', PhoneIncoming], ['manual', 'Other', PenLine]].map(([v, l, Ic]) => (
                  <button key={v} onClick={() => setChannel(v)} data-testid={`manual-response-channel-${v}`}
                    className={`text-[10px] px-2 py-1 rounded-sm border inline-flex items-center gap-1 transition-colors ${channel === v ? 'border-sky-500/60 bg-sky-500/15 text-sky-300' : 'border-border text-muted-foreground hover:text-foreground'}`}>
                    <Ic className="w-3 h-3" /> {l}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Result</p>
              <div className="flex gap-1.5">
                <button onClick={() => setSentiment('positive')} data-testid="manual-response-positive"
                  className={`text-[10px] px-2 py-1 rounded-sm border inline-flex items-center gap-1 transition-colors ${sentiment === 'positive' ? 'border-emerald-500/60 bg-emerald-500/15 text-emerald-300' : 'border-border text-muted-foreground hover:text-foreground'}`}>
                  <ThumbsUp className="w-3 h-3" /> Interested
                </button>
                <button onClick={() => setSentiment('negative')} data-testid="manual-response-negative"
                  className={`text-[10px] px-2 py-1 rounded-sm border inline-flex items-center gap-1 transition-colors ${sentiment === 'negative' ? 'border-red-500/60 bg-red-500/15 text-red-300' : 'border-border text-muted-foreground hover:text-foreground'}`}>
                  <ThumbsDown className="w-3 h-3" /> Not interested
                </button>
              </div>
            </div>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
              placeholder="Optional note (e.g. decline reason)…" data-testid="manual-response-note"
              className="w-full bg-secondary/40 border border-border rounded-sm px-3 py-2 text-xs text-foreground focus:outline-none focus:border-sky-700 resize-none" />
            <button onClick={save} disabled={busy} data-testid="manual-response-save"
              className="w-full inline-flex items-center justify-center gap-2 bg-sky-700 hover:bg-sky-600 disabled:opacity-40 text-white rounded-sm text-xs h-9 transition-colors">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Reply className="w-3.5 h-3.5" />}
              Log response
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const NI_VIA = {
  optout: { label: 'Opt-out', cls: 'bg-amber-500/10 border-amber-500/30 text-amber-300' },
  phone: { label: 'Phone', cls: 'bg-red-500/10 border-red-500/30 text-red-300' },
  email: { label: 'E-Mail', cls: 'bg-sky-500/10 border-sky-500/30 text-sky-300' },
  inbound_call: { label: 'Rückruf', cls: 'bg-sky-500/10 border-sky-500/30 text-sky-300' },
  letter: { label: 'Brief', cls: 'bg-sky-500/10 border-sky-500/30 text-sky-300' },
  manual: { label: 'Manuell', cls: 'bg-sky-500/10 border-sky-500/30 text-sky-300' },
};

function NoInterestSection({ data }) {
  if (!data || data.total === 0) return null;
  const cats = Object.entries(data.by_category || {}).sort((a, b) => b[1] - a[1]);
  const ch = data.by_channel || {};
  return (
    <div className="bg-card border border-border rounded-sm" data-testid="no-interest-section">
      <div className="px-5 py-3.5 border-b border-border">
        <h2 className="text-sm font-medium text-foreground inline-flex items-center gap-2">
          <PhoneOutgoing className="w-4 h-4 text-red-400" /> "No interest" — why owners decline
        </h2>
        <div className="flex flex-wrap items-center gap-1.5 mt-1.5" data-testid="ni-channel-split">
          <span className="text-[10px] text-muted-foreground">{data.total} decline{data.total !== 1 ? 's' : ''}:</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded-sm border bg-amber-500/10 border-amber-500/30 text-amber-300" data-testid="ni-channel-optout">
            {ch.optout || 0}× Opt-out (Landing Page)
          </span>
          <span className="text-[10px] px-1.5 py-0.5 rounded-sm border bg-red-500/10 border-red-500/30 text-red-300" data-testid="ni-channel-phone">
            {ch.phone || 0}× Phone follow-up
          </span>
          <span className="text-[10px] px-1.5 py-0.5 rounded-sm border bg-sky-500/10 border-sky-500/30 text-sky-300" data-testid="ni-channel-written"
            title="Negative Antworten per E-Mail, Brief oder Rückruf (KI-Bewertung oder Daumen runter) — eine pro Deal">
            {ch.written || 0}× E-Mail / Brief
          </span>
        </div>
      </div>
      <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-3">
          {cats.map(([c, n]) => (
            <div key={c} data-testid={`ni-cat-${c}`}>
              <div className="flex items-center gap-2 text-xs text-foreground">
                {NI_CAT_LABELS[c] || c}
                <span className="ml-auto font-mono text-muted-foreground">{n} · {pct(n, data.total)}%</span>
              </div>
              <div className="h-1.5 bg-secondary/40 rounded-full mt-1.5 overflow-hidden">
                <div className="h-full bg-red-700/80 rounded-full" style={{ width: `${pct(n, data.total)}%` }} />
              </div>
            </div>
          ))}
        </div>
        <div className="space-y-2 max-h-56 overflow-y-auto">
          {(data.reasons || []).map((r, i) => (
            <div key={i} className="border border-border/60 rounded-sm px-3 py-2 bg-secondary/20" data-testid={`ni-reason-${i}`}>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-foreground truncate">{r.company_name}</span>
                <span className={`text-[8px] uppercase tracking-wider px-1 py-0.5 rounded-sm border shrink-0 ${(NI_VIA[r.via] || NI_VIA.email).cls}`}
                  data-testid={`ni-reason-via-${i}`}>
                  {(NI_VIA[r.via] || NI_VIA.email).label}
                </span>
                <span className="text-[9px] uppercase tracking-wider text-red-400/80 ml-auto shrink-0">{NI_CAT_LABELS[r.category] || 'No category'}</span>
              </div>
              {r.reason && <p className="text-[11px] text-muted-foreground mt-0.5">{r.reason}</p>}
              <p className="text-[9px] font-mono text-muted-foreground/50 mt-0.5">{fmt(r.at)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const tooltipStyle = {
  contentStyle: { background: '#0f1826', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, fontSize: 11 },
  labelStyle: { color: '#94a3b8' },
};

export default function OutreachStatsPage() {
  const navigate = useNavigate();
  const { isAdmin, isAcMember, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState(null);
  const [showResetAll, setShowResetAll] = useState(false);
  const [showLogResponse, setShowLogResponse] = useState(false);
  const [lettersRange, setLettersRange] = useState('month'); // 'month' | 'week'
  const [channelMenu, setChannelMenu] = useState(null);
  const [sentimentFilter, setSentimentFilter] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [dealGroups, setDealGroups] = useState({ active: true, passive: false });
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => {
    api.getLandingAnalytics().then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.exportOutreachStats();
      const name = saveBlobResponse(res, 'OLTA_Outreach-Statistik.xlsx');
      toast.success(`${name} — ${res.headers?.['x-response-count'] || 0} Antworten, ${res.headers?.['x-letter-count'] || 0} Briefe, 7 Blätter`);
    } catch (err) { toast.error((await blobErrorDetail(err)) || 'Export fehlgeschlagen'); }
    finally { setExporting(false); }
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.getWorkflowAssignments().then(r => setAssignments(r.data)).catch(() => {});
  }, []);
  const canEditLetters = isAdmin
    || !!(user && assignments?.letter_drafter?.id === user.id)
    || !!(user && assignments?.letter_approver?.id === user.id);

  const handleReset = async (d) => {
    if (!window.confirm(`Reset statistics for "${d.company_name}"? All scans and responses will be deleted — the QR link stays valid.`)) return;
    try {
      await api.resetLandingStats(d.landing_id);
      toast.success('Statistics reset');
      load();
    } catch { toast.error('Reset failed'); }
  };

  const handleDelete = async (d) => {
    if (!window.confirm(`Delete the landing page for "${d.company_name}" completely? The QR code on printed letters will stop working!`)) return;
    try {
      await api.deleteLanding(d.landing_id);
      toast.success('Landing page deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-6 h-6 text-blue-500 animate-spin" /></div>;
  if (!data) return <div className="text-sm text-muted-foreground p-8">Could not load statistics.</div>;

  const { funnel, scans, responses, per_deal: perDeal, letters } = data;
  const channels = data.response_channels || { via_landing: 0, via_email: 0, via_phone: 0, via_manual: 0 };
  // Deal-based funnel (Max, 04.11.2026): one deal counts once even when it got two letters; break-ups never count.
  const dealsSent = funnel.deals_sent ?? funnel.letters_sent;
  const dealsScanned = funnel.deals_scanned ?? funnel.letters_scanned ?? funnel.pages_scanned;
  const dealsResponded = funnel.deals_responded ?? funnel.letters_responded ?? funnel.pages_responded;
  const multiLetter = funnel.deals_multi_letter || 0;
  const funnelSteps = [
    { label: 'Sent', value: dealsSent, sub: `${funnel.letters_sent} letters · baseline (100%)`, cls: 'bg-slate-600/70' },
    { label: 'Scanned', value: dealsScanned, sub: `${pct(dealsScanned, dealsSent)}% of deals`, cls: 'bg-sky-800/80' },
    { label: 'Responded', value: dealsResponded, sub: `${pct(dealsResponded, dealsSent)}% of deals`, cls: 'bg-emerald-700/80' },
  ];
  const maxFunnel = Math.max(...funnelSteps.map(s => s.value), 1);
  const hourData = scans.by_hour.filter(h => h.hour >= 6 && h.hour <= 22).map(h => ({ ...h, label: `${h.hour}h` }));
  const weekdayData = scans.by_weekday.map(w => ({ ...w, label: WEEKDAYS[w.weekday] }));
  const respWeekly = (data.weekly || []).map(w => ({
    label: 'KW ' + w.week.slice(-2),
    value: sentimentFilter === 'positive' ? (w.resp_pos || 0) : sentimentFilter === 'negative' ? (w.resp_neg || 0) : (w.responded || 0),
    rate: sentimentFilter === 'positive' ? (w.rate_pos || 0) : sentimentFilter === 'negative' ? (w.rate_neg || 0) : (w.rate || 0),
  }));

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="outreach-stats-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-foreground tracking-tight flex items-center gap-2.5">
            <QrCode className="w-7 h-7 text-sky-400" /> Outreach Statistics
          </h1>
          <p className="text-muted-foreground text-sm mt-1">QR scans, landing page engagement and entrepreneur responses across all deals</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isAcMember && (
            <button onClick={() => navigate('/outreach-stats/clean')} data-testid="outreach-clean-stats-btn"
              title="Bereinigte Auswertung: Scans nur nach Versandtag, erste Response je Deal, Inbound vs. Call-getriggert, A/B-Tests mit Wilson-CI + Fisher-Test, Kohorten, CSV"
              className="inline-flex items-center gap-1.5 text-[11px] text-sky-300 border border-sky-700/50 hover:bg-sky-500/10 rounded-sm px-2.5 py-1.5 transition-colors">
              <Flag className="w-3 h-3" /> Bereinigte Auswertung
            </button>
          )}
          <button onClick={exportExcel} disabled={exporting} data-testid="outreach-export-btn"
            title="Alle Kennzahlen dieser Seite + jede Antwort und jeder Brief mit Deal-Stammdaten (OLTA-Sektor, Sektor, Umsatz, EBITDA, MA, Stage, Owner) als Excel"
            className="inline-flex items-center gap-1.5 text-[11px] text-emerald-300 border border-emerald-700/50 hover:bg-emerald-500/10 rounded-sm px-2.5 py-1.5 transition-colors disabled:opacity-50">
            {exporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileSpreadsheet className="w-3 h-3" />} Excel export
          </button>
          {!isAcMember && <LetterTemplateDialog canEdit={canEditLetters} />}
          {isAdmin && (
            <button onClick={() => setShowResetAll(true)} data-testid="outreach-reset-all-btn"
              className="inline-flex items-center gap-1.5 text-[11px] text-red-400/80 hover:text-red-300 border border-red-900/50 hover:border-red-800 rounded-sm px-2.5 py-1.5 transition-colors">
              <RotateCcw className="w-3 h-3" /> Reset statistics
            </button>
          )}
        </div>
      </div>
      <ResetAllDialog open={showResetAll} onOpenChange={setShowResetAll} data={data} onDone={load} />
      <ManualResponseDialog open={showLogResponse} onOpenChange={setShowLogResponse} onDone={load} />

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={Send} label="Deals contacted" value={dealsSent}
          sub={`${funnel.letters_sent} letters sent${multiLetter ? ` · ${multiLetter} re-sent to the same deal` : ''}${funnel.breakups_sent ? ` · ${funnel.breakups_sent} break-ups not counted` : ''}`} />
        <Kpi icon={Eye} label="Deals scanned" value={dealsScanned} color="text-emerald-400"
          sub={`deals with ≥1 QR scan · ${scans.total} scans total (incl. repeats)`} />
        <Kpi icon={Reply} label="Responses" value={dealsResponded} color="text-amber-400"
          sub={`${funnel.letters_responded} responded letter${funnel.letters_responded === 1 ? '' : 's'}: ${channels.via_landing} landing · ${channels.via_email ?? 0} email · ${channels.via_call ?? 0} call · ${channels.via_phone + channels.via_manual} other`} />
        <Kpi icon={CalendarClock} label="Response rate" value={`${pct(dealsResponded, dealsSent)}%`} sub="of contacted deals · max. 1 letter per deal" />
      </div>

      {isAdmin && <VestaboardCard />}

      {/* Response time — how long owners take to respond after the letter went out */}
      {data.response_time && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="response-time-kpis">
          <Kpi icon={Timer} label="Ø Response time" value={fmtDays(data.response_time.mean_days)}
            sub={`mean across ${data.response_time.count} response${data.response_time.count !== 1 ? 's' : ''} — sent → response`} />
          <Kpi icon={Timer} label="Median response time" value={fmtDays(data.response_time.median_days)}
            sub="half of the owners respond faster" />
          <Kpi icon={Timer} label="Fastest response" value={fmtDays(data.response_time.min_days)} color="text-emerald-400"
            sub={data.response_time.min_company || ''} />
          <Kpi icon={Timer} label="Slowest response" value={fmtDays(data.response_time.max_days)} color="text-amber-400"
            sub={data.response_time.max_company || ''} />
        </div>
      )}

      {/* Funnel + response channels */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-card border border-border rounded-sm p-5" data-testid="outreach-funnel">
          <h2 className="text-sm font-medium text-foreground mb-4">Outreach funnel</h2>
          <div className="space-y-2.5">
            {funnelSteps.map((s) => (
              <div key={s.label} className="flex items-center gap-3">
                <span className="w-32 text-[11px] text-muted-foreground shrink-0">{s.label}</span>
                <div className="flex-1 h-6 bg-secondary/40 rounded-sm overflow-hidden">
                  <div className={`h-full rounded-sm flex items-center px-2 transition-all ${s.cls}`}
                    style={{ width: `${Math.max((s.value / maxFunnel) * 100, s.value > 0 ? 8 : 2)}%` }}>
                    <span className="text-[11px] font-mono text-white font-medium">{s.value}</span>
                  </div>
                </div>
                <span className="w-28 text-[10px] font-mono text-muted-foreground shrink-0 text-right">{s.sub}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-card border border-border rounded-sm p-5" data-testid="response-channels">
          <h2 className="text-sm font-medium text-foreground mb-1">Where responses come from</h2>
          <p className="text-[10px] text-muted-foreground mb-2">
            {funnel.letters_responded ?? 0} responded letter{(funnel.letters_responded ?? 0) !== 1 ? 's' : ''}
          </p>
          {data.response_sentiment && (funnel.letters_responded ?? 0) > 0 && (
            <div className="flex items-center gap-1.5 mb-3 flex-wrap" data-testid="response-sentiment-summary">
              <button onClick={() => setSentimentFilter(f => f === 'positive' ? null : 'positive')}
                data-testid="response-sentiment-positive"
                title="Click to see where the positive responses came from"
                className={`text-[10px] font-mono px-1.5 py-0.5 rounded border inline-flex items-center gap-1 cursor-pointer transition-all ${sentimentFilter === 'positive'
                  ? 'border-emerald-400 bg-emerald-500/25 text-emerald-300 ring-1 ring-emerald-400/50'
                  : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'}`}>
                <ThumbsUp className="w-2.5 h-2.5" /> {data.response_sentiment.positive} positive
              </button>
              <button onClick={() => setSentimentFilter(f => f === 'negative' ? null : 'negative')}
                data-testid="response-sentiment-negative"
                title="Click to see where the negative responses came from"
                className={`text-[10px] font-mono px-1.5 py-0.5 rounded border inline-flex items-center gap-1 cursor-pointer transition-all ${sentimentFilter === 'negative'
                  ? 'border-red-400 bg-red-500/25 text-red-300 ring-1 ring-red-400/50'
                  : 'border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20'}`}>
                <ThumbsDown className="w-2.5 h-2.5" /> {data.response_sentiment.negative} negative
              </button>
              {data.response_sentiment.unclassified > 0 && (
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-border text-muted-foreground"
                  title="Rate responses on the letter card (Outreach page)">
                  {data.response_sentiment.unclassified} unrated
                </span>
              )}
            </div>
          )}
          {(funnel.letters_responded ?? 0) === 0 ? (
            <p className="text-xs text-muted-foreground">No responses yet.</p>
          ) : (() => {
            const filtered = sentimentFilter ? (data.all_responses || []).filter(r => r.sentiment === sentimentFilter) : null;
            const denom = filtered ? filtered.length : (funnel.letters_responded || 0);
            const rows = [
              { key: 'landing', icon: QrCode, label: 'QR landing page', total: channels.via_landing, color: 'bg-sky-700', hint: 'callback, booking or phone tap + call via the personal page' },
              { key: 'email', icon: Mail, label: 'Email reply', total: channels.via_email ?? 0, color: 'bg-emerald-600', hint: 'inbound email reply detected on the deal' },
              { key: 'inbound_call', icon: PhoneIncoming, label: 'Inbound call', total: channels.via_call ?? 0, color: 'bg-violet-600', hint: 'owner dialled our number manually' },
              { key: 'phone', icon: PhoneOutgoing, label: 'Phone follow-up', total: channels.via_phone, color: 'bg-orange-600', hint: 'follow-up call result — interested or declined' },
              { key: 'manual', icon: PenLine, label: 'Logged manually', total: channels.via_manual, color: 'bg-slate-600', hint: 'via "Response received" on the letter' },
            ];
            const triggers = data.response_triggers || {};
            const triggerRows = [
              { key: 'initial_letter', icon: Send, label: 'After the initial letter', total: triggers.initial_letter ?? 0, color: 'bg-sky-600', hint: 'no follow-up had gone out yet' },
              { key: 'email_followup', icon: Mail, label: 'After the email follow-up', total: triggers.email_followup ?? 0, color: 'bg-emerald-600', hint: 'sequence email was the last touch' },
              { key: 'phone_followup', icon: PhoneOutgoing, label: 'After the phone follow-up', total: triggers.phone_followup ?? 0, color: 'bg-orange-600', hint: 'our call was the last touch' },
              { key: 'linkedin_followup', icon: Linkedin, label: 'After the LinkedIn follow-up', total: triggers.linkedin_followup ?? 0, color: 'bg-blue-600', hint: 'LinkedIn request was the last touch' },
              { key: 'breakup_letter', icon: Flag, label: 'After the break-up letter', total: triggers.breakup_letter ?? 0, color: 'bg-rose-600', hint: 'break-up letter was the last touch' },
            ];
            const chipCls = (r) => r.sentiment === 'positive'
              ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20'
              : r.sentiment === 'negative'
                ? 'border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20'
                : 'border-border bg-secondary/60 text-foreground/80 hover:bg-secondary';
            const renderRows = (items, matcher, prefix) => items.map(c => {
              const rowId = `${prefix}-${c.key}`;
              const allList = (data.all_responses || []).filter(r => matcher(r) === c.key);
              const expanded = !!expandedRows[rowId];
              const list = filtered ? filtered.filter(r => matcher(r) === c.key) : (expanded ? allList : null);
              const value = filtered ? (list || []).length : c.total;
              const clickable = !filtered && allList.length > 0;
              return (
                <div key={c.label} data-testid={rowId}>
                  <div onClick={() => clickable && setExpandedRows(x => ({ ...x, [rowId]: !x[rowId] }))}
                    data-testid={`${rowId}-toggle`}
                    role={clickable ? 'button' : undefined}
                    title={clickable ? 'Click to see which companies these responses belong to' : undefined}
                    className={`flex items-center gap-2 text-xs text-foreground ${clickable ? 'cursor-pointer hover:text-sky-300 transition-colors' : ''}`}>
                    <c.icon className="w-3.5 h-3.5 text-muted-foreground" />
                    {c.label}
                    {clickable && <ChevronDown className={`w-3 h-3 text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`} />}
                    <span className="ml-auto font-mono text-muted-foreground">{value} · {pct(value, denom)}%</span>
                  </div>
                  <div className="h-1.5 bg-secondary/40 rounded-full mt-1.5 overflow-hidden">
                    <div className={`h-full rounded-full ${c.color}`} style={{ width: `${pct(value, denom)}%` }} />
                  </div>
                  {list ? (
                    list.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {list.map((r, j) => (
                          <button key={j} onClick={() => r.project_id && navigate(`/projects/${r.project_id}`)}
                            data-testid={`${rowId}-company-${j}`}
                            title={`Open deal${r.at ? ` — responded ${fmt(r.at)}` : ''}`}
                            className={`text-[9px] px-1.5 py-0.5 rounded-sm border transition-colors max-w-[180px] truncate ${chipCls(r)}`}>
                            {r.company_name || '—'}
                          </button>
                        ))}
                      </div>
                    )
                  ) : (
                    <p className="text-[9px] text-muted-foreground/60 mt-0.5">{c.hint}</p>
                  )}
                </div>
              );
            });
            return (
              <div className="space-y-3">
                {sentimentFilter && (
                  <p className={`text-[10px] font-mono ${sentimentFilter === 'positive' ? 'text-emerald-400' : 'text-red-400'}`}
                    data-testid="sentiment-filter-note">
                    {denom} {sentimentFilter} response{denom !== 1 ? 's' : ''} — click the badge again to show all
                  </p>
                )}
                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">How the response came in</p>
                {renderRows(rows, r => r.channel, 'response-channel')}
                <div className="border-t border-border pt-3 mt-4">
                  <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-3"
                    title="Which touch was the owner responding to — the last outreach touch (incl. real call attempts) before the response">
                    What they were responding to
                  </p>
                  <div className="space-y-3" data-testid="response-triggers">
                    {renderRows(triggerRows, r => r.trigger, 'response-trigger')}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Phone call attempts — how many dials it takes to reach an owner */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="call-attempts-kpis">
        <Kpi icon={PhoneOutgoing} label="Call attempts" value={data.call_stats?.total_attempts ?? 0}
          sub={`across ${data.call_stats?.letters_called ?? 0} owner${(data.call_stats?.letters_called ?? 0) !== 1 ? 's' : ''} called — logged results, completed sequence calls and outgoing Webex calls`} />
        <Kpi icon={PhoneIncoming} label="Owners reached" value={data.call_stats?.reached ?? 0} color="text-emerald-400"
          sub="call ended with a clear result" />
        <Kpi icon={PhoneOutgoing} label="Ø attempts to reach" value={data.call_stats?.avg_attempts_to_reach ?? '—'}
          sub="dials per reached owner" />
        <Kpi icon={CalendarClock} label="Not reached yet" value={data.call_stats?.unreached ?? 0} color="text-amber-400"
          sub={'marked "later" — keep trying'} />
      </div>

      {/* Break-up letters — the last touch before formally passing a deal */}
      {(data.breakup?.sent ?? 0) > 0 && (
        <div className="bg-card border border-border rounded-sm p-5" data-testid="breakup-stats-section">
          <div className="flex items-center gap-2 mb-1">
            <Flag className="w-4 h-4 text-rose-400" />
            <h2 className="text-sm font-medium text-foreground">Break-up letters — the last touch</h2>
          </div>
          <p className="text-[10px] text-muted-foreground mb-3">
            Responses after a break-up letter count here. Deals without a response stay in the waiting window before you formally pass them.
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <Kpi icon={Send} label="Break-ups sent" value={data.breakup.sent} />
            <Kpi icon={Reply} label="Responded after break-up" value={data.breakup.responded_after} color="text-emerald-400"
              sub="response came in after the break-up went out" />
            <Kpi icon={Timer} label="Still waiting" value={(data.breakup.waiting || []).length} color="text-amber-400"
              sub="no response yet — pass the deal once the waiting window is over" />
          </div>
          {(data.breakup.waiting || []).length > 0 && (
            <div className="mt-3 space-y-0.5 max-h-[220px] overflow-y-auto pr-1" data-testid="breakup-waiting-list">
              {(data.breakup.waiting || []).map((w, i) => (
                <div key={w.project_id || i} className="flex items-center gap-2 text-xs py-1.5 px-2 rounded-sm hover:bg-secondary/30 transition-colors">
                  <button onClick={() => w.project_id && navigate(`/projects/${w.project_id}`)}
                    data-testid={`breakup-waiting-${i}`}
                    title="Open deal"
                    className="text-foreground hover:text-rose-300 transition-colors font-medium truncate max-w-[280px] text-left">
                    {w.company_name || '—'}
                  </button>
                  <span className="ml-auto font-mono text-muted-foreground shrink-0">
                    {w.days_since != null ? `${w.days_since}d since break-up` : '—'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Mailbox scan — which inboxes (incl. kontakt@) feed the email responses */}
      {!isAcMember && <MailboxScanCard />}
      {!isAcMember && <TestScanAuditCard onChanged={load} />}

      {/* All responses — every responded letter with channel, rating and AI summary */}
      <div className="bg-card border border-border rounded-sm p-5" data-testid="all-responses-section">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h2 className="text-sm font-medium text-foreground">All responses ({(data.all_responses || []).length})</h2>
          <button onClick={() => setShowLogResponse(true)} data-testid="log-response-btn"
            className="inline-flex items-center gap-1.5 text-[11px] text-sky-300 border border-sky-700/50 hover:bg-sky-500/10 rounded-sm px-2.5 py-1.5 transition-colors shrink-0">
            <Plus className="w-3 h-3" /> Log response
          </button>
        </div>
        <p className="text-[10px] text-muted-foreground mb-3">Every owner response — newest first, with channel, rating and AI summary. Missing one (e.g. a call)? Log it manually.</p>
        {(data.all_responses || []).length === 0 ? (
          <p className="text-xs text-muted-foreground">No responses yet.</p>
        ) : (
          <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
            {(data.all_responses || []).map((r, i) => {
              const ch = {
                landing: { icon: QrCode, label: 'Landing page', cls: 'text-sky-400 bg-sky-400/10 border-sky-500/30' },
                email: { icon: Mail, label: 'Email', cls: 'text-emerald-400 bg-emerald-400/10 border-emerald-500/30' },
                inbound_call: { icon: PhoneIncoming, label: 'Inbound call', cls: 'text-violet-300 bg-violet-400/10 border-violet-500/30' },
                phone: { icon: PhoneOutgoing, label: 'Phone', cls: 'text-orange-400 bg-orange-400/10 border-orange-500/30' },
                manual: { icon: PenLine, label: 'Manual', cls: 'text-slate-300 bg-slate-400/10 border-slate-500/30' },
              }[r.channel] || { icon: PenLine, label: r.channel, cls: 'text-muted-foreground bg-secondary border-border' };
              const ChIcon = ch.icon;
              const trig = {
                initial_letter: { label: 'after initial letter', cls: 'text-sky-300/80 border-sky-500/25' },
                email_followup: { label: 'after email follow-up', cls: 'text-emerald-300/80 border-emerald-500/25' },
                phone_followup: { label: 'after phone follow-up', cls: 'text-orange-300/80 border-orange-500/25' },
                linkedin_followup: { label: 'after LinkedIn follow-up', cls: 'text-blue-300/80 border-blue-500/25' },
                breakup_letter: { label: 'after break-up letter', cls: 'text-rose-300/80 border-rose-500/25' },
              }[r.trigger];
              return (
                <div key={r.letter_id || i} className="flex items-start gap-2.5 py-2 px-2 rounded-sm hover:bg-secondary/30 transition-colors flex-wrap"
                  data-testid={`response-row-${i}`}>
                  <button onClick={() => r.project_id && navigate(`/projects/${r.project_id}`)}
                    className="text-xs font-medium text-foreground hover:text-blue-400 transition-colors min-w-[140px] text-left truncate max-w-[220px]"
                    data-testid={`response-row-company-${i}`}>
                    {r.company_name || '—'}
                  </button>
                  {r.channel !== 'landing' && r.letter_id ? (
                    <span className="relative shrink-0">
                      <button onClick={() => setChannelMenu(channelMenu === i ? null : i)}
                        data-testid={`response-channel-btn-${i}`}
                        title="Click to correct the channel"
                        className={`text-[9px] px-1.5 py-0.5 rounded border font-mono uppercase inline-flex items-center gap-1 cursor-pointer hover:brightness-125 transition-all ${ch.cls}`}>
                        <ChIcon className="w-2.5 h-2.5" /> {ch.label} <ChevronDown className="w-2.5 h-2.5 opacity-60" />
                      </button>
                      {channelMenu === i && (
                        <span className="absolute left-0 top-6 z-30 bg-card border border-border rounded-sm shadow-xl py-1 flex flex-col min-w-[130px]"
                          data-testid={`response-channel-menu-${i}`}>
                          {[['email', 'Email', Mail], ['inbound_call', 'Inbound call', PhoneIncoming], ['phone', 'Phone follow-up', PhoneOutgoing], ['manual', 'Manual', PenLine]].map(([v, l, Ic]) => (
                            <button key={v} onClick={async () => {
                              setChannelMenu(null);
                              try { await api.updateResponseMeta(r.letter_id, { channel: v }); toast.success(`Channel set to ${l}`); load(); }
                              catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
                            }} data-testid={`response-channel-opt-${v}-${i}`}
                              className={`text-left text-[10px] px-2.5 py-1.5 hover:bg-secondary/60 inline-flex items-center gap-1.5 ${r.channel === v ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                              <Ic className="w-3 h-3" /> {l}
                            </button>
                          ))}
                          <p className="text-[9px] uppercase tracking-wider text-muted-foreground/70 px-2.5 pt-1.5 pb-0.5 border-t border-border mt-1">Antwort kam auf …</p>
                          {[['initial_letter', 'Erstbrief', Send], ['email_followup', 'E-Mail-Follow-up', Mail], ['phone_followup', 'Anruf (Phone follow-up)', Phone], ['linkedin_followup', 'LinkedIn-Follow-up', Linkedin], ['breakup_letter', 'Break-up-Brief', Flag]].map(([v, l, Ic]) => (
                            <button key={v} onClick={async () => {
                              setChannelMenu(null);
                              try { await api.updateResponseMeta(r.letter_id, { trigger: v }); toast.success(`${r.company_name}: Antwort auf ${l} — Statistik aktualisiert`); load(); }
                              catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
                            }} data-testid={`response-trigger-opt-${v}-${i}`}
                              className={`text-left text-[10px] px-2.5 py-1.5 hover:bg-secondary/60 inline-flex items-center gap-1.5 ${r.trigger === v ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                              <Ic className="w-3 h-3" /> {l}{r.trigger === v && r.trigger_override ? <span className="text-[8px] text-amber-300 ml-1">korrigiert</span> : null}
                            </button>
                          ))}
                          {r.trigger_override && (
                            <button onClick={async () => {
                              setChannelMenu(null);
                              try { await api.updateResponseMeta(r.letter_id, { trigger: null }); toast.success('Auslöser wieder automatisch'); load(); }
                              catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
                            }} data-testid={`response-trigger-auto-${i}`}
                              className="text-left text-[10px] px-2.5 py-1.5 hover:bg-secondary/60 inline-flex items-center gap-1.5 text-muted-foreground">
                              <RotateCcw className="w-3 h-3" /> Auslöser automatisch bestimmen
                            </button>
                          )}
                          <button onClick={async () => {
                            setChannelMenu(null);
                            if (!window.confirm(`${r.company_name}: Antwort zurücknehmen? Der Brief gilt wieder als „versendet“ und zählt nicht mehr als Antwort (z. B. Rückruf der Assistenz nach unserem Anruf).`)) return;
                            try {
                              const res = await api.undoLetterResponse(r.letter_id, 'Rückruf nach unserem Anruf — kein Gespräch mit dem Inhaber');
                              toast.success(`Antwort zurückgenommen — ${r.company_name} zählt nicht mehr${res.data.hint ? `. ${res.data.hint}` : ''}`, { duration: 8000 });
                              load();
                            } catch (err) { toast.error(err.response?.data?.detail || 'Zurücknehmen fehlgeschlagen'); }
                          }} data-testid={`response-undo-${i}`}
                            className="text-left text-[10px] px-2.5 py-1.5 hover:bg-red-500/10 inline-flex items-center gap-1.5 text-red-300 border-t border-border mt-1 whitespace-nowrap">
                            <X className="w-3 h-3" /> Keine Antwort (Rückruf) — zurücksetzen
                          </button>
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className={`text-[9px] px-1.5 py-0.5 rounded border font-mono uppercase inline-flex items-center gap-1 shrink-0 ${ch.cls}`}>
                      <ChIcon className="w-2.5 h-2.5" /> {ch.label}
                    </span>
                  )}
                  {trig && (
                    <span data-testid={`response-trigger-badge-${i}`}
                      title={`Touch #${r.touch_number || 1}${r.call_attempts ? ` · ${r.call_attempts} call attempt${r.call_attempts !== 1 ? 's' : ''}` : ''}${r.trigger_override ? ' · manuell korrigiert' : ' · automatisch: letzter Touch vor der Antwort — im Kanal-Menü korrigierbar'}`}
                      className={`text-[9px] px-1.5 py-0.5 rounded border bg-transparent inline-flex items-center gap-1 shrink-0 ${trig.cls}`}>
                      {trig.label}{r.trigger_override ? <PenLine className="w-2.5 h-2.5 opacity-70" /> : null}
                    </span>
                  )}
                  {r.letter_id ? (
                    <span className="inline-flex items-center gap-1 shrink-0">
                      {[['positive', ThumbsUp, 'text-emerald-400 bg-emerald-400/15 border-emerald-500/40', 'Interested'],
                        ['negative', ThumbsDown, 'text-red-400 bg-red-400/15 border-red-500/40', 'Not interested']].map(([v, Ic, activeCls, title]) => (
                        <button key={v} title={title}
                          onClick={async () => {
                            try {
                              const res = await api.updateResponseMeta(r.letter_id, { sentiment: r.sentiment === v ? null : v });
                              toast.success(r.sentiment === v ? 'Rating removed'
                                : res.data?.response_ai ? `${title} — AI hat die Antwort neu gelesen & zusammengefasst` : title);
                              load();
                            } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
                          }}
                          data-testid={`response-rate-${v}-${i}`}
                          className={`p-1 rounded border transition-all ${r.sentiment === v ? activeCls : 'text-muted-foreground/50 border-border hover:text-foreground hover:border-muted-foreground/40'}`}>
                          <Ic className="w-3 h-3" />
                        </button>
                      ))}
                    </span>
                  ) : r.sentiment ? (
                    <span className={`text-[9px] px-1.5 py-0.5 rounded border font-mono uppercase inline-flex items-center gap-1 shrink-0 ${r.sentiment === 'positive'
                      ? 'text-emerald-400 bg-emerald-400/10 border-emerald-500/30' : 'text-red-400 bg-red-400/10 border-red-500/30'}`}>
                      {r.sentiment === 'positive' ? <ThumbsUp className="w-2.5 h-2.5" /> : <ThumbsDown className="w-2.5 h-2.5" />}
                      {r.sentiment === 'positive' ? 'Positive' : 'Negative'}
                    </span>
                  ) : null}
                  <span className="text-[11px] text-muted-foreground flex-1 min-w-[200px] italic">
                    {r.ai_summary
                      ? <>AI: {r.ai_summary}</>
                      : r.lead_type
                        ? <>{r.lead_type === 'appointment' ? 'Appointment booked' : 'Callback requested'}{r.lead_name ? ` by ${r.lead_name}` : ''}</>
                        : ''}
                  </span>
                  <span className="text-[10px] font-mono text-muted-foreground shrink-0 ml-auto">
                    {r.response_days != null && (
                      <span className="text-sky-400/90 mr-2" title="Time between letter sent and response"
                        data-testid={`response-days-${i}`}>⏱ {fmtDays(r.response_days)} after send</span>
                    )}
                    {fmt(r.at)}
                  </span>
                  {r.letter_id && (
                    <button onClick={async () => {
                      if (!window.confirm(`Response von ${r.company_name || 'dieser Firma'} entfernen (z. B. Out-of-Office)? Der Brief zählt wieder als versendet und fliegt aus allen Statistiken.`)) return;
                      try {
                        await api.unmarkResponse(r.letter_id);
                        toast.success('Response entfernt — zählt nicht mehr in den Statistiken');
                        load();
                      } catch (err) { toast.error(err.response?.data?.detail || 'Entfernen fehlgeschlagen'); }
                    }} data-testid={`response-remove-btn-${i}`}
                      title="Fehl-Response entfernen (z. B. Out-of-Office-Autoreply)"
                      className="text-muted-foreground/40 hover:text-red-400 transition-colors p-0.5 shrink-0">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* No interest reasons (Point 8) */}
      <NoInterestSection data={data.no_interest} />

      {/* Sequence performance — response attribution per channel */}
      <SequencePerformanceCard />

      {/* Monthly / weekly letter volume */}
      {(((lettersRange === 'week' ? data.weekly : data.monthly) || []).length > 0) && (
        <div className="bg-card border border-border rounded-sm p-5" data-testid="monthly-letters-chart">
          <div className="flex items-start justify-between gap-3 mb-1">
            <h2 className="text-sm font-medium text-foreground">
              {lettersRange === 'week' ? 'Letters per week — last 12 weeks' : 'Letters per month — last 12 months'}
            </h2>
            <div className="inline-flex rounded-sm border border-border overflow-hidden shrink-0" data-testid="letters-range-toggle">
              <button
                type="button"
                onClick={() => setLettersRange('week')}
                data-testid="letters-range-week"
                className={`text-[10px] uppercase tracking-wider px-2.5 py-1 transition-colors ${lettersRange === 'week' ? 'bg-sky-600/20 text-sky-300' : 'bg-transparent text-muted-foreground hover:text-foreground'}`}
              >Week</button>
              <button
                type="button"
                onClick={() => setLettersRange('month')}
                data-testid="letters-range-month"
                className={`text-[10px] uppercase tracking-wider px-2.5 py-1 border-l border-border transition-colors ${lettersRange === 'month' ? 'bg-sky-600/20 text-sky-300' : 'bg-transparent text-muted-foreground hover:text-foreground'}`}
              >Month</button>
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground mb-3">Bars: letters sent · created</p>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart
              data={(lettersRange === 'week'
                ? data.weekly.map(w => ({ ...w, label: 'KW ' + w.week.slice(-2) }))
                : data.monthly.map(m => ({ ...m, label: m.month.slice(5) + '/' + m.month.slice(2, 4) }))
              )}
              margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#64748b' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 9, fill: '#64748b' }} allowDecimals={false} axisLine={false} tickLine={false} />
              <RTooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 2, fontSize: 11 }}
                labelStyle={{ color: '#94a3b8' }} />
              <Bar dataKey="sent" name="Sent" fill="#0284c7" radius={[2, 2, 0, 0]} maxBarSize={28} />
              <Bar dataKey="created" name="Created" fill="#334155" radius={[2, 2, 0, 0]} maxBarSize={28} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Responses over time — bars per week, line = cumulative response rate, thumbs filter */}
      {(data.weekly || []).length > 0 && (
        <div className="bg-card border border-border rounded-sm p-5" data-testid="responses-weekly-chart">
          <div className="flex items-start justify-between gap-3 mb-1">
            <h2 className="text-sm font-medium text-foreground">
              {sentimentFilter === 'positive' ? 'Positive responses' : sentimentFilter === 'negative' ? 'Negative responses' : 'Responses'} — last 12 weeks
            </h2>
            <div className="inline-flex items-center gap-1.5 shrink-0" data-testid="responses-sentiment-toggle">
              <button onClick={() => setSentimentFilter(f => f === 'positive' ? null : 'positive')}
                data-testid="responses-filter-positive" title="Nur positive Responses"
                className={`p-1.5 rounded border transition-all ${sentimentFilter === 'positive'
                  ? 'text-emerald-300 bg-emerald-500/25 border-emerald-400 ring-1 ring-emerald-400/50'
                  : 'text-muted-foreground/60 border-border hover:text-emerald-400 hover:border-emerald-500/40'}`}>
                <ThumbsUp className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => setSentimentFilter(f => f === 'negative' ? null : 'negative')}
                data-testid="responses-filter-negative" title="Nur negative Responses"
                className={`p-1.5 rounded border transition-all ${sentimentFilter === 'negative'
                  ? 'text-red-300 bg-red-500/25 border-red-400 ring-1 ring-red-400/50'
                  : 'text-muted-foreground/60 border-border hover:text-red-400 hover:border-red-500/40'}`}>
                <ThumbsDown className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground mb-3">
            Bars: {sentimentFilter ? `${sentimentFilter} responses` : 'responses'} per week · Line: response rate in % (kumulativ)
          </p>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={respWeekly} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#64748b' }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="n" tick={{ fontSize: 9, fill: '#64748b' }} allowDecimals={false} axisLine={false} tickLine={false} />
              <YAxis yAxisId="rate" orientation="right" width={40} tickFormatter={(v) => `${v}%`}
                tick={{ fontSize: 9, fill: '#f59e0b' }} axisLine={false} tickLine={false} />
              <RTooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 2, fontSize: 11 }}
                labelStyle={{ color: '#94a3b8' }} formatter={(v, name) => name === 'Response rate' ? [`${v} %`, name] : [v, name]} />
              <Bar yAxisId="n" dataKey="value" name="Responses"
                fill={sentimentFilter === 'positive' ? '#10b981' : sentimentFilter === 'negative' ? '#ef4444' : '#0284c7'}
                radius={[2, 2, 0, 0]} maxBarSize={28} />
              <Line yAxisId="rate" dataKey="rate" name="Response rate" stroke="#f59e0b" strokeWidth={2} dot={{ r: 2.5 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* When do they scan */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-sm p-5">
          <h2 className="text-sm font-medium text-foreground mb-3">Scans by hour of day</h2>
          <ResponsiveContainer width="100%" height={140}>
            <BarChart data={hourData} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#64748b' }} interval={1} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 9, fill: '#64748b' }} allowDecimals={false} axisLine={false} tickLine={false} />
              <RTooltip {...tooltipStyle} />
              <Bar dataKey="scans" name="Scans" fill="#0e7490" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-card border border-border rounded-sm p-5">
          <h2 className="text-sm font-medium text-foreground mb-3">Scans by weekday</h2>
          <ResponsiveContainer width="100%" height={140}>
            <BarChart data={weekdayData} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#64748b' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 9, fill: '#64748b' }} allowDecimals={false} axisLine={false} tickLine={false} />
              <RTooltip {...tooltipStyle} />
              <Bar dataKey="scans" name="Scans" fill="#0e7490" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Per-deal table — grouped into Active (engagement) / Passive (nothing yet) */}
      <div className="bg-card border border-border rounded-sm" data-testid="per-deal-table">
        <div className="px-5 py-3.5 border-b border-border">
          <h2 className="text-sm font-medium text-foreground">Per deal</h2>
        </div>
        {perDeal.length === 0 ? (
          <p className="text-xs text-muted-foreground p-6">No landing pages yet — create one from a letter on the Outreach page.</p>
        ) : (
          [
            { key: 'active', label: 'Active', desc: 'engagement tracked — scans or responses', dot: 'bg-emerald-500',
              deals: perDeal.filter(d => (d.scans > 0) || (d.callbacks > 0) || (d.appointments > 0)) },
            { key: 'passive', label: 'Passive', desc: 'no scans or engagement yet', dot: 'bg-slate-600',
              deals: perDeal.filter(d => !((d.scans > 0) || (d.callbacks > 0) || (d.appointments > 0))) },
          ].map(g => (
            <div key={g.key} className="border-b border-border/60 last:border-b-0">
              <button onClick={() => setDealGroups(s => ({ ...s, [g.key]: !s[g.key] }))}
                data-testid={`per-deal-group-${g.key}`}
                className="w-full flex items-center gap-2 px-5 py-2.5 hover:bg-secondary/20 transition-colors text-left">
                {dealGroups[g.key] ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${g.dot}`} />
                <span className="text-xs font-medium text-foreground">{g.label}</span>
                <span className="text-[10px] text-muted-foreground hidden sm:inline">{g.desc}</span>
                <span className="ml-auto text-[10px] font-mono text-muted-foreground shrink-0">{g.deals.length} deal{g.deals.length !== 1 ? 's' : ''}</span>
              </button>
              {dealGroups[g.key] && (g.deals.length === 0 ? (
                <p className="text-[11px] text-muted-foreground px-5 pb-3">No deals in this group.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="text-[9px] uppercase tracking-wider text-muted-foreground border-b border-border/60">
                        <th className="px-5 py-2.5 font-medium">Company</th>
                        <th className="px-3 py-2.5 font-medium">Letter</th>
                        <th className="px-3 py-2.5 font-medium">Media</th>
                        <th className="px-3 py-2.5 font-medium text-right">Scans</th>
                        <th className="px-3 py-2.5 font-medium">First scan</th>
                        <th className="px-3 py-2.5 font-medium">Last scan</th>
                        <th className="px-3 py-2.5 font-medium text-right">Callbacks</th>
                        <th className="px-3 py-2.5 font-medium text-right">Appointments</th>
                        <th className="px-3 py-2.5" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/40">
                      {g.deals.map(d => (
                        <tr key={d.landing_id} className="hover:bg-secondary/20 transition-colors" data-testid={`stats-deal-${d.landing_id}`}>
                          <td className="px-5 py-2.5 text-xs font-medium text-foreground max-w-[220px] truncate">{d.company_name}</td>
                          <td className="px-3 py-2.5 text-[10px] font-mono text-muted-foreground capitalize">{d.letter_status || '—'}</td>
                          <td className="px-3 py-2.5">
                            <span className="inline-flex items-center gap-1.5">
                              {d.has_audio && <Mic className="w-3 h-3 text-emerald-400" />}
                              {d.has_video && <Video className="w-3 h-3 text-sky-400" />}
                              {!d.has_audio && !d.has_video && <span className="text-[10px] text-muted-foreground/50">—</span>}
                            </span>
                          </td>
                          <td className={`px-3 py-2.5 text-xs font-mono text-right ${d.scans > 0 ? 'text-emerald-400' : 'text-muted-foreground/50'}`}>{d.scans}</td>
                          <td className="px-3 py-2.5 text-[10px] font-mono text-muted-foreground whitespace-nowrap">
                            {fmt(d.first_scan_at)}
                            {d.days_to_first_scan !== null && d.days_to_first_scan !== undefined && (
                              <span className="text-muted-foreground/50"> ({d.days_to_first_scan}d)</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-[10px] font-mono text-muted-foreground whitespace-nowrap">{fmt(d.last_scan_at)}</td>
                          <td className={`px-3 py-2.5 text-xs font-mono text-right ${d.callbacks > 0 ? 'text-amber-400' : 'text-muted-foreground/50'}`}>{d.callbacks}</td>
                          <td className={`px-3 py-2.5 text-xs font-mono text-right ${d.appointments > 0 ? 'text-amber-400' : 'text-muted-foreground/50'}`}>{d.appointments}</td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-1.5 justify-end">
                              {isAdmin && (
                                <>
                                  <button onClick={() => handleReset(d)} title="Reset statistics (scans + responses)"
                                    data-testid={`stats-reset-${d.landing_id}`}
                                    className="text-muted-foreground/40 hover:text-amber-400 transition-colors">
                                    <RotateCcw className="w-3.5 h-3.5" />
                                  </button>
                                  <button onClick={() => handleDelete(d)} title="Delete landing page (QR link dies!)"
                                    data-testid={`stats-delete-${d.landing_id}`}
                                    className="text-muted-foreground/40 hover:text-red-400 transition-colors">
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </>
                              )}
                              <button onClick={() => navigate(`/projects/${d.project_id}`)} title="Open deal"
                                data-testid={`stats-open-${d.landing_id}`}
                                className="text-muted-foreground/40 hover:text-sky-400 transition-colors">
                                <ChevronRight className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
